import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * Chrome for a form-bearing dialog: the Dialog + Content + titled Header.
 * The caller passes its `<form>` (or `<Form>`) as `children`, including the
 * footer submit/cancel buttons — those stay inside the form so they pick up
 * native submit and `formState.isSubmitting`. Use `ConfirmDialog` instead for
 * the confirm/destructive shape (it owns the footer buttons).
 */
export type FormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Extra className on the DialogContent (e.g. a max-width override). */
  contentClassName?: string;
  /**
   * `false` makes the dialog required: no close button, and Escape / an
   * outside click do nothing. The only way out is the form inside it.
   * Defaults to `true`.
   */
  dismissible?: boolean;
  children: React.ReactNode;
};

export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  contentClassName,
  dismissible = true,
  children,
}: FormDialogProps) {
  const block = (e: Event) => e.preventDefault();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('max-w-md', contentClassName)}
        showCloseButton={dismissible}
        onEscapeKeyDown={dismissible ? undefined : block}
        onPointerDownOutside={dismissible ? undefined : block}
        onInteractOutside={dismissible ? undefined : block}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}
